import React, { useState } from "react";
import { Compass, PenTool, GraduationCap, Gamepad2, Sparkle } from "lucide-react";

const ICONS = [Compass, PenTool, GraduationCap, Gamepad2, Sparkle];

/**
 * Overlapping / fanned pastel banners with a fixed interaction model.
 *
 * The trick:
 *   - Each card sits under a small transparent HEADER STRIP (the top slice
 *     of the *next* card that overlaps it). Those overlapping strips would
 *     normally steal hover events from the card visually below them.
 *   - We give every card an increasing z-index (deeper card → higher z).
 *   - On hover we bump the hovered card to the top and expand it fully so
 *     the correct card responds. Non-hovered cards recede & dim slightly.
 */
const Expertise = ({ site }) => {
  const areas = site?.expertise_areas || [];
  const [active, setActive] = useState(null);
  if (areas.length === 0) return null;

  const OVERLAP = 22; // Small overlap: just enough to show layering.
                     // MUST be < each card's top-padding (py-6 ≈ 24px) so
                     // the covered zone is empty padding, not the title.

  return (
    <section id="expertise" className="relative py-16 md:py-24 bg-[color:var(--bg-2)]">
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <div className="mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-[color:var(--muted)]">
            <Sparkle className="w-3.5 h-3.5 text-[color:var(--violet)]" />
            Skill &amp; Expertise
          </div>
          <div className="mt-3 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <h2 className="font-display text-3xl md:text-5xl font-semibold leading-[1.05]">
              Areas of <em className="italic text-[color:var(--violet)]">expertise</em>.
            </h2>
            <p className="max-w-md text-[color:var(--ink-soft)]">
              A few disciplines I return to again and again &mdash; each one shapes how I
              think about design.
            </p>
          </div>
        </div>

        <div
          data-testid="expertise-stack"
          className="relative mx-auto"
          style={{ maxWidth: 820 }}
        >
          {areas.map((a, i) => {
            const Icon = ICONS[i % ICONS.length];
            const rotate = i % 2 === 0 ? -0.8 : 1.0;
            const isActive = active === i;
            const isDimmed = active !== null && active !== i;
            const isLast = i === areas.length - 1;

            return (
              <div
                key={a.title + i}
                data-testid={`expertise-card-${i}`}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive((v) => (v === i ? null : v))}
                onFocus={() => setActive(i)}
                onBlur={() => setActive((v) => (v === i ? null : v))}
                tabIndex={0}
                className="expertise-card relative rounded-[22px] border border-black/10 px-6 pt-7 pb-6 md:px-8 md:pt-8 md:pb-7 cursor-pointer outline-none"
                style={{
                  backgroundColor: a.color || "#E1D8FF",
                  // Later cards sit ON TOP so the overlap always covers the
                  // bottom edge of the card underneath (never a title).
                  zIndex: isActive ? 999 : 10 + i,
                  marginTop: i === 0 ? 0 : `-${OVERLAP}px`,
                  transform: isActive
                    ? "translateY(-10px) rotate(0deg) scale(1.01)"
                    : `translateY(0) rotate(${rotate}deg)`,
                  filter: isDimmed ? "brightness(0.96) saturate(0.92)" : "none",
                  opacity: isDimmed ? 0.9 : 1,
                  // Shadow lives on the BOTTOM edge of every card except the
                  // last, so you can clearly see the next card is stacked
                  // on top of it.
                  boxShadow: isActive
                    ? "0 22px 44px -18px rgba(20,20,20,0.32), 0 -1px 0 0 rgba(255,255,255,0.35) inset"
                    : isLast
                    ? "0 6px 18px -12px rgba(20,20,20,0.25)"
                    : "0 14px 20px -14px rgba(20,20,20,0.35), 0 -1px 0 0 rgba(255,255,255,0.35) inset",
                  transition:
                    "transform 380ms cubic-bezier(.2,.9,.25,1.2), margin 380ms cubic-bezier(.2,.9,.25,1.2), box-shadow 300ms ease, opacity 250ms ease, filter 250ms ease",
                }}
              >
                <div className="flex items-start gap-4 text-[color:#141414]">
                  <div className="shrink-0 w-11 h-11 rounded-2xl bg-white/70 grid place-items-center border border-black/10">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-display italic font-semibold text-xl md:text-2xl leading-tight">
                      {a.title}
                    </h3>
                    {a.description && (
                      <p
                        className="mt-1.5 text-sm md:text-[15px] text-neutral-800/90 leading-relaxed transition-all duration-300"
                        style={{
                          maxHeight: isActive ? 200 : 42,
                          overflow: "hidden",
                        }}
                      >
                        {a.description}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 text-center text-xs text-[color:var(--muted)]">
          Hover a banner to expand it.
        </div>
      </div>
    </section>
  );
};

export default Expertise;
